import React, { useEffect, useId, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';

const TOOLTIP_OFFSET = 10;
const VIEWPORT_PADDING = 8;

const InfoTooltip = ({ children, className = '', placement = 'top', text, title = '' }) => {
  const triggerRef = useRef(null);
  const tooltipRef = useRef(null);
  const [visible, setVisible] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0 });

  const reactId = useId();
  const tooltipId = useMemo(() => `tooltip-${reactId.replace(/:/g, '')}`, [reactId]);

  useEffect(() => {
    if (!visible || !triggerRef.current || !tooltipRef.current) return;

    const triggerRect = triggerRef.current.getBoundingClientRect();
    const viewportWidth = window.innerWidth;

    const tooltipRect = tooltipRef.current.getBoundingClientRect();
    const triggerCenterX = triggerRect.left + triggerRect.width / 2;
    const minCenterX = VIEWPORT_PADDING + tooltipRect.width / 2;
    const maxCenterX = viewportWidth - VIEWPORT_PADDING - tooltipRect.width / 2;
    const clampedCenterX = Math.max(
      minCenterX,
      Math.min(triggerCenterX, maxCenterX)
    );

    setPosition({
      top: triggerRect.top - TOOLTIP_OFFSET,
      left: clampedCenterX,
    });
  }, [visible, placement, text]);

  useEffect(() => {
    if (!visible) return;

    const handleOutsideClick = (event) => {
      if (!triggerRef.current || !tooltipRef.current) return;

      const clickedTrigger = triggerRef.current.contains(event.target);
      const clickedTooltip = tooltipRef.current.contains(event.target);

      if (!clickedTrigger && !clickedTooltip) {
        setVisible(false);
      }
    };

    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        setVisible(false);
      }
    };

    document.addEventListener('mousedown', handleOutsideClick);
    document.addEventListener('keydown', handleEscape);

    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [visible]);

  return (
    <>
      <span
        ref={triggerRef}
        aria-describedby={visible ? tooltipId : undefined}
        className={className}
        onMouseEnter={() => setVisible(true)}
        onMouseLeave={() => setVisible(false)}
        onFocus={() => setVisible(true)}
        onBlur={() => setVisible(false)}
        onClick={(event) => {
          event.stopPropagation();
          setVisible((prev) => !prev);
        }}
        style={{ display: 'inline-flex' }}
      >
        {children}
      </span>
      {visible &&
        createPortal(
          <div
            ref={tooltipRef}
            id={tooltipId}
            role='tooltip'
            className={`tooltip top in`}
            style={{
              display: 'block',
              top: `${position.top}px`,
              left: `${position.left}px`,
              position: 'fixed',
              transform: 'translate(-50%, -100%)',
              zIndex: 1080,
              maxWidth: 'calc(100vw - 16px)',
            }}
          >
            <div className='tooltip-arrow'></div>
            <div className='tooltip-inner' style={{ whiteSpace: 'normal', wordBreak: 'break-word', maxHeight: 'calc(100vh - 16px)', overflowY: 'auto' }}>
              {title && <div style={{ fontWeight: 700, marginBottom: '4px' }}>{title}</div>}
              {text}
            </div>
          </div>,
          document.body
        )}
    </>
  );
};

InfoTooltip.propTypes = {
  children: PropTypes.node.isRequired,
  className: PropTypes.string,
  placement: PropTypes.oneOf(['top', 'bottom']),
  title: PropTypes.string,
  text: PropTypes.string.isRequired,
};

export default InfoTooltip;
